/**
 * Which company this portal is for, known before anyone logs in.
 *
 * Reads GET /api/public/org (resolved from the host, or from ?org=<slug> /
 * localStorage on localhost so a developer can act as a partner) and exposes
 * it to every page: name, logo, colors, support contact, demo state. It also
 * titles the tab, swaps the manifest and favicon, and pins the X-WK-Org header
 * for local development.
 *
 *   const { org, name, isPlatform, demoMode } = useOrg();
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { actAsSlug } from '../utils/actAs';

const API = process.env.REACT_APP_API_URL || '';
const DEV_ORG_KEY = 'wk_org';

/** What the portal shows while loading, and forever on White Knight when the API is unreachable. */
export const PLATFORM_ORG = Object.freeze({
  id: null,
  slug: 'wkmc',
  kind: 'platform',
  isPlatform: true,
  name: 'White Knight Motor Club',
  legalName: 'White Knight Motor Club, LLC',
  customerName: 'White Knight Roadside Services',
  logoUrl: '/images/logo.png',
  iconUrl: null,
  colors: { primary: '#1976d2', secondary: '#dc004e' },
  supportEmail: 'support@wkmc.services',
  supportPhone: '(214) 814-5675',
  social: {},
  appLinks: {},
  content: { tagline: '', heroText: '' },
  urls: { portal: '', customerSite: 'https://wkroadss.com', applicationForm: '/applicationform' },
  demoMode: false,
  setupComplete: true,
  features: {}
});

const OrgContext = createContext({ org: PLATFORM_ORG, loading: true, refresh: () => {} });

const isLocalHost = () => /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname);

/** On localhost a developer picks the company with ?org=acme (remembered) or ?org= to clear. */
export const devOrgSlug = () => {
  if (!isLocalHost()) return null;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.has('org')) {
      const v = params.get('org');
      if (v) localStorage.setItem(DEV_ORG_KEY, v); else localStorage.removeItem(DEV_ORG_KEY);
    }
    return localStorage.getItem(DEV_ORG_KEY) || null;
  } catch {
    return null;
  }
};

const setLink = (rel, href) => {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) { el = document.createElement('link'); el.rel = rel; document.head.appendChild(el); }
  el.href = href;
};

export const OrgProvider = ({ children }) => {
  const [org, setOrg] = useState(PLATFORM_ORG);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const slug = devOrgSlug();
    if (slug) axios.defaults.headers.common['X-WK-Org'] = slug;
    else delete axios.defaults.headers.common['X-WK-Org'];

    // A White Knight owner acting as a partner sees that company's name, logo and colors
    const shown = actAsSlug() || slug;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/public/org`, { params: shown ? { org: shown } : undefined });
        if (!cancelled && data && data.slug) setOrg({ ...PLATFORM_ORG, ...data, colors: { ...PLATFORM_ORG.colors, ...(data.colors || {}) } });
      } catch (e) {
        // Unreachable API or an old backend: stay White Knight
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tick]);

  // Tab title, manifest and icon follow the company
  useEffect(() => {
    if (loading) return;
    document.title = org.name;
    if (!org.isPlatform) {
      const shown = actAsSlug() || devOrgSlug();
      setLink('manifest', `${API}/api/public/org/manifest.webmanifest${shown ? `?org=${encodeURIComponent(shown)}` : ''}`);
      if (org.iconUrl) {
        document.querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"]').forEach((el) => { el.href = org.iconUrl; });
      }
      const meta = document.querySelector('meta[name="theme-color"]');
      if (meta) meta.content = org.colors.primary;
    }
  }, [org, loading]);

  const value = useMemo(() => ({
    org,
    loading,
    refresh: () => setTick((t) => t + 1),
    isPlatform: org.isPlatform !== false,
    name: org.name,
    customerName: org.customerName || org.name,
    legalName: org.legalName || org.name,
    logoUrl: org.logoUrl,
    colors: org.colors,
    supportEmail: org.supportEmail, // provider-facing (portal, agreements)
    supportPhone: org.supportPhone,
    customerSupportEmail: org.customerSupportEmail || org.supportEmail,
    customerSupportPhone: org.customerSupportPhone || org.supportPhone,
    applicantSupportEmail: org.applicantSupportEmail || org.supportEmail,
    applicantSupportPhone: org.applicantSupportPhone || org.supportPhone,
    demoMode: !!org.demoMode,
    urls: org.urls || PLATFORM_ORG.urls
  }), [org, loading]);

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
};

export const useOrg = () => useContext(OrgContext);

export default OrgContext;
